<template>
  <div class="min-vh-100 d-flex align-items-center bg-light">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-6 col-lg-5">
          <div class="card border-0 shadow-lg">
            <div class="card-body p-5">
              <div class="text-center mb-4">
                <h2 class="fw-bold text-primary mb-2">Reimposta Password</h2>
                <p class="text-muted">
                  Inserisci la tua nuova password per completare il reset.
                </p>
              </div>

              <form @submit.prevent="handleResetPassword">
                <!-- Success Message -->
                <div v-if="successMessage" class="alert alert-success d-flex align-items-center mb-4" role="alert">
                  <i class="material-symbols-outlined me-2">check_circle</i>
                  <span>{{ successMessage }}</span>
                </div>

                <!-- API Error Message -->
                <div v-if="apiError" class="alert alert-danger d-flex align-items-center mb-4" role="alert">
                  <i class="material-symbols-outlined me-2">error</i>
                  <span>{{ apiError }}</span>
                </div>

                <!-- Token/Email validation error -->
                <div v-if="tokenError" class="alert alert-warning d-flex align-items-center mb-4" role="alert">
                  <i class="material-symbols-outlined me-2">warning</i>
                  <span>{{ tokenError }}</span>
                </div>

                <div v-if="!resetCompleted && !tokenError">
                  <!-- Email field (readonly) -->
                  <div class="form-floating mb-3">
                    <input
                      type="email"
                      class="form-control"
                      id="resetEmail"
                      :value="email"
                      disabled
                    />
                    <label for="resetEmail">Email</label>
                  </div>

                  <!-- New Password -->
                  <div class="form-floating mb-3 position-relative">
                    <input
                      :type="passwordFieldType"
                      class="form-control"
                      :class="{ 'is-invalid': passwordError }"
                      id="newPassword"
                      placeholder="Nuova password"
                      v-model="password"
                      @blur="validatePassword"
                      @input="validatePassword"
                      required
                    />
                    <label for="newPassword">Nuova Password</label>
                    <button
                      type="button"
                      class="btn border-0 position-absolute top-50 end-0 translate-middle-y d-flex align-items-center justify-content-center"
                      @click="togglePasswordVisibility"
                      style="z-index: 10;"
                    >
                      <i class="material-symbols-outlined">
                        {{ isPasswordVisible ? 'visibility_off' : 'visibility' }}
                      </i>
                    </button>
                    <div v-if="passwordError" class="invalid-feedback">
                      {{ passwordError }}
                    </div>
                  </div>

                  <!-- Confirm Password -->
                  <div class="form-floating mb-4 position-relative">
                    <input
                      :type="confirmPasswordFieldType"
                      class="form-control"
                      :class="{ 'is-invalid': confirmPasswordError }"
                      id="confirmPassword"
                      placeholder="Conferma password"
                      v-model="passwordConfirmation"
                      @blur="validatePasswordConfirmation"
                      @input="validatePasswordConfirmation"
                      required
                    />
                    <label for="confirmPassword">Conferma Password</label>
                    <button
                      type="button"
                      class="btn border-0 position-absolute top-50 end-0 translate-middle-y d-flex align-items-center justify-content-center"
                      @click="toggleConfirmPasswordVisibility"
                      style="z-index: 10;"
                    >
                      <i class="material-symbols-outlined">
                        {{ isConfirmPasswordVisible ? 'visibility_off' : 'visibility' }}
                      </i>
                    </button>
                    <div v-if="confirmPasswordError" class="invalid-feedback">
                      {{ confirmPasswordError }}
                    </div>
                  </div>

                  <!-- Submit Button -->
                  <div class="d-grid mb-4">
                    <button
                      type="submit"
                      class="btn btn-primary btn-lg"
                      :disabled="!isFormValid || isLoading"
                    >
                      <div v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      {{ isLoading ? 'Resettando...' : 'Reimposta Password' }}
                    </button>
                  </div>
                </div>

                <!-- Success State -->
                <div v-if="resetCompleted" class="text-center">
                  <p class="text-muted mb-4">
                    La tua password è stata cambiata con successo. Torna alla pagina precedente per continuare il login.
                  </p>
                  <!--<RouterLink
                    to="/authentication/login"
                    class="btn btn-primary"
                  >
                    Vai al Login
                  </RouterLink>-->
                </div>

                <!-- Invalid Token State -->
                <div v-if="tokenError" class="text-center">
                  <div class="mb-4">
                    <i class="material-symbols-outlined text-warning" style="font-size: 4rem;">error</i>
                  </div>
                  <p class="text-muted mb-4">
                    Il link non è valido o è scaduto. Richiedi un nuovo reset password.
                  </p>
                  <RouterLink
                    to="/authentication/login"
                    class="btn btn-primary"
                  >
                    Torna al Login
                  </RouterLink>
                </div>
              </form>
            </div>
          </div>

          <!-- Footer -->
          <div class="text-center mt-4">
            <!--<p class="text-muted small">
              Ti sei ricordato la password?
              <RouterLink to="/authentication/login" class="text-decoration-none">
                Torna al Login
              </RouterLink>
            </p>-->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import authService from "@/services/authService";
import { formatErrorMessage } from "@/utils/errorUtils";

export default defineComponent({
  name: "SimpleResetPasswordPage",
  setup() {
    const route = useRoute();

    const token = ref('');
    const email = ref('');
    const password = ref('');
    const passwordConfirmation = ref('');
    const passwordError = ref('');
    const confirmPasswordError = ref('');
    const apiError = ref('');
    const successMessage = ref('');
    const tokenError = ref('');
    const isLoading = ref(false);
    const resetCompleted = ref(false);

    const isPasswordVisible = ref(false);
    const isConfirmPasswordVisible = ref(false);
    const passwordFieldType = ref('password');
    const confirmPasswordFieldType = ref('password');

    const togglePasswordVisibility = () => {
      isPasswordVisible.value = !isPasswordVisible.value;
      passwordFieldType.value = isPasswordVisible.value ? 'text' : 'password';
    };

    const toggleConfirmPasswordVisibility = () => {
      isConfirmPasswordVisible.value = !isConfirmPasswordVisible.value;
      confirmPasswordFieldType.value = isConfirmPasswordVisible.value ? 'text' : 'password';
    };

    const validatePassword = () => {
      if (!password.value) {
        passwordError.value = 'La password è obbligatoria';
        return false;
      }
      if (password.value.length < 8) {
        passwordError.value = 'La password deve essere di almeno 8 caratteri';
        return false;
      }
      passwordError.value = '';
      return true;
    };

    const validatePasswordConfirmation = () => {
      if (!passwordConfirmation.value) {
        confirmPasswordError.value = 'La conferma password è obbligatoria';
        return false;
      }
      if (password.value !== passwordConfirmation.value) {
        confirmPasswordError.value = 'Le password non coincidono';
        return false;
      }
      confirmPasswordError.value = '';
      return true;
    };

    const isFormValid = computed(() => {
      return password.value &&
             passwordConfirmation.value &&
             !passwordError.value &&
             !confirmPasswordError.value &&
             token.value &&
             email.value;
    });

    const handleResetPassword = async () => {
      if (!validatePassword() || !validatePasswordConfirmation()) {
        return;
      }

      isLoading.value = true;
      apiError.value = '';
      successMessage.value = '';

      try {
        const response = await authService.resetPassword({
          token: token.value,
          email: email.value,
          password: password.value,
          password_confirmation: passwordConfirmation.value
        });

        // Check for success - Laravel might return different response structures
        if (response.status === 200 || response.data.status === 200) {
          successMessage.value = response.data.message || 'Password reimpostata con successo! Ora puoi effettuare il login con la nuova password.';
          resetCompleted.value = true;
        }
      } catch (error: any) {
        console.error("Reset password failed:", error);
        apiError.value = formatErrorMessage(error);
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      // Extract token and email from URL parameters
      token.value = (route.query.token as string) || '';
      email.value = (route.query.email as string) || '';

      // Validate required parameters
      if (!token.value || !email.value) {
        tokenError.value = 'Link di reset non valido. Token o email mancanti.';
      }

      // Set page title
      document.title = 'Reimposta Password';
      document.body.classList.add("bg-light");
    });

    return {
      token,
      email,
      password,
      passwordConfirmation,
      passwordError,
      confirmPasswordError,
      apiError,
      successMessage,
      tokenError,
      isLoading,
      resetCompleted,
      isPasswordVisible,
      isConfirmPasswordVisible,
      passwordFieldType,
      confirmPasswordFieldType,
      togglePasswordVisibility,
      toggleConfirmPasswordVisibility,
      isFormValid,
      validatePassword,
      validatePasswordConfirmation,
      handleResetPassword
    };
  },
});
</script>

<style scoped>
.material-symbols-outlined {
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
}

.btn:focus {
  box-shadow: none;
}

.card {
  border-radius: 1rem;
}

.form-floating > label {
  padding-left: 1rem;
}

.form-floating > .form-control {
  padding-left: 1rem;
  padding-right: 3rem;
}

.position-relative .btn {
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  background: none;
  color: #6c757d;
}

.position-relative .btn:hover {
  color: #495057;
}
</style>